import type { FC, ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface WeddingCoverProps {
    couple: string
    image: string | null
    imageAlt: string | null
    accentColor: string | null
    loading?: 'lazy' | 'eager'
    className?: string
}

const HEX_COLOR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i
const FALLBACK_COLOR = '#6e1423'

const initialsOf = (couple: string): Array<string> => couple
    .split(/\s*(?:&|\+|\band\b)\s*/i)
    .map(name => name.trim().charAt(0).toUpperCase())
    .filter(Boolean)
    .slice(0, 2)

// The wedding's cover photo from the CMS. Until one is uploaded, it shows a
// monogram of the couple's initials on the wedding's accent colour.
export const WeddingCover: FC<WeddingCoverProps> = ({
    couple,
    image,
    imageAlt,
    accentColor,
    loading = 'lazy',
    className
}): ReactNode => {
    if (image) {
        return (
            // eslint-disable-next-line @next/next/no-img-element
            <img
                src={image}
                alt={imageAlt || `A moment from the wedding of ${couple}`}
                loading={loading}
                decoding='async'
                className={cn('size-full object-cover', className)}
            />
        )
    }

    const color = accentColor && HEX_COLOR.test(accentColor) ? accentColor : FALLBACK_COLOR
    const [first, second] = initialsOf(couple)

    return (
        <div
            aria-hidden='true'
            style={{ backgroundColor: color }}
            className={cn('relative flex size-full items-center justify-center overflow-hidden', className)}
        >
            <div className='pattern-jaali absolute inset-0 text-white/12' />
            <div className='absolute inset-0 bg-gradient-to-b from-black/0 via-black/5 to-black/30' />
            <div className='absolute inset-[7%] rounded-t-full border border-[#f7e7c6]/35' />
            <div className='absolute inset-[11%] rounded-t-full border border-[#f7e7c6]/20' />

            <div className='relative flex flex-col items-center text-[#f7e7c6]'>
                <span className='text-xs font-medium uppercase tracking-[0.4em] opacity-80'>
                    Shubh vivah
                </span>

                <span className='mt-3 font-heading text-6xl italic leading-none sm:text-7xl'>
                    {first}
                    {second && (
                        <>
                            <span className='mx-2 align-middle font-heading text-3xl not-italic opacity-80 sm:text-4xl'>&amp;</span>
                            {second}
                        </>
                    )}
                </span>
            </div>
        </div>
    )
}
